/**
 * Mock data for the Buy vs Build interactive mindmap.
 * Executive-level content designed to impress Fortune 500 decision-makers.
 */

export interface MindmapNode {
  id: string;
  title: string;
  subtitle?: string;
  type: 'root' | 'branch' | 'leaf';
  sentiment?: 'positive' | 'negative' | 'neutral';
  summary: string;
  content: string;
  detailContent?: {
    tradeoffs: { gain: string; giveUp: string }[];
    questions: string[];
    vendorInsights?: string[];
  };
  children?: MindmapNode[];
}

export interface AnalysisMessage {
  text: string;
  duration: number;
}

// Messages shown during the AI analysis loading phase
export const ANALYSIS_MESSAGES: AnalysisMessage[] = [
  { text: "Synthesizing vendor evaluation insights...", duration: 800 },
  { text: "Mapping strategic decision pathways...", duration: 700 },
  { text: "Analyzing organizational readiness factors...", duration: 800 },
  { text: "Calculating build vs buy tradeoffs...", duration: 700 },
  { text: "Evaluating long-term capability implications...", duration: 800 },
  { text: "Generating strategic recommendations...", duration: 600 },
];

// The complete mindmap data structure
export const MINDMAP_DATA: MindmapNode = {
  id: 'root',
  title: 'AI Strategy',
  subtitle: 'Buy vs Build Decision Framework',
  type: 'root',
  summary: 'Strategic pathways for AI capability development',
  content: 'Based on your vendor evaluation, here are the strategic pathways to consider. Each path offers distinct advantages and trade-offs that align differently with organizational priorities.',
  children: [
    {
      id: 'build',
      title: 'BUILD',
      subtitle: 'In-House Development',
      type: 'branch',
      sentiment: 'neutral',
      summary: 'Develop AI capabilities internally with your own team',
      content: 'Building in-house gives you complete control over your AI stack, but requires significant investment in talent, infrastructure, and time. Best suited for organizations where AI is a core strategic differentiator.',
      detailContent: {
        tradeoffs: [
          { gain: 'Complete intellectual property ownership', giveUp: '12-18 month time to initial value' },
          { gain: 'Unlimited customization potential', giveUp: 'Significant upfront capital investment' },
          { gain: 'Deep organizational AI competency', giveUp: 'Ongoing talent acquisition challenges' },
        ],
        questions: [
          'Do we have access to AI/ML talent in our market?',
          'Is AI a core differentiator or an enabler for our business?',
          'What is our tolerance for extended development timelines?',
          'Can we commit to continuous R&D investment?',
        ],
        vendorInsights: [
          'Your evaluated vendors scored low on transparency (SEE) - building may give you the visibility you need',
          'The customization limitations you identified would not exist with an in-house solution',
        ],
      },
      children: [
        {
          id: 'build-control',
          title: 'Full Control',
          type: 'leaf',
          sentiment: 'positive',
          summary: 'Own the IP, customize infinitely',
          content: 'With an in-house build, every line of code, every model weight, and every optimization belongs to you. You can customize prompts, fine-tune models on proprietary data, and pivot your approach as AI technology evolves—without waiting for a vendor roadmap.',
          detailContent: {
            tradeoffs: [
              { gain: 'Proprietary algorithms become competitive moats', giveUp: 'Must build everything from foundational components' },
              { gain: 'Can optimize for your specific use cases', giveUp: 'No benefit from vendor R&D investments' },
            ],
            questions: [
              'What specific customizations do we need that vendors cannot provide?',
              'How would we leverage proprietary AI as a competitive advantage?',
            ],
          },
        },
        {
          id: 'build-team',
          title: 'Team Investment',
          type: 'leaf',
          sentiment: 'positive',
          summary: 'Build lasting AI competency internally',
          content: 'An in-house team accumulates institutional knowledge that compounds over time. Your engineers understand your data, your customers, and your business context in ways external vendors never will. This deep expertise becomes a strategic asset.',
          detailContent: {
            tradeoffs: [
              { gain: 'Institutional AI knowledge that stays with the company', giveUp: 'Salary costs 2-3x traditional engineering roles' },
              { gain: 'Team understands business context deeply', giveUp: 'Key person dependencies and retention risk' },
            ],
            questions: [
              'What is our current AI/ML talent bench strength?',
              'Can we offer competitive compensation and interesting problems?',
              'How will we handle knowledge transfer if key people leave?',
            ],
          },
        },
        {
          id: 'build-time',
          title: 'Time to Value',
          type: 'leaf',
          sentiment: 'negative',
          summary: 'Longer runway but lasting capability',
          content: 'Building takes time—typically 12-18 months to reach parity with commercial solutions. However, each month of development builds capabilities that are yours permanently. The question is whether your competitive timeline allows for this investment.',
          detailContent: {
            tradeoffs: [
              { gain: 'Capabilities compound and improve over years', giveUp: 'Competitors using vendors may move faster initially' },
              { gain: 'No dependency on vendor release schedules', giveUp: 'Must solve problems vendors have already solved' },
            ],
            questions: [
              'What is our competitive timeline for AI capabilities?',
              'Can we afford to be 12-18 months behind faster-moving competitors?',
              'Is there a phased approach that delivers incremental value?',
            ],
          },
        },
        {
          id: 'build-costs',
          title: 'Hidden Costs',
          type: 'leaf',
          sentiment: 'negative',
          summary: 'Talent, infrastructure, maintenance',
          content: 'The sticker price of building is deceptive. Beyond engineering salaries, factor in: GPU infrastructure ($500K-2M/year), MLOps tooling, data labeling, security compliance, and the opportunity cost of your best engineers not working on core product.',
          detailContent: {
            tradeoffs: [
              { gain: 'Transparent cost structure you control', giveUp: 'Total cost often 3-5x initial estimates' },
              { gain: 'Can optimize spend over time', giveUp: 'Ongoing infrastructure and maintenance burden' },
            ],
            questions: [
              'Have we budgeted for GPU/compute infrastructure?',
              'What is the fully-loaded cost of an AI team (not just salaries)?',
              'Who will maintain systems when the original builders move on?',
            ],
          },
        },
      ],
    },
    {
      id: 'buy',
      title: 'BUY',
      subtitle: 'Vendor Solution',
      type: 'branch',
      sentiment: 'neutral',
      summary: 'License proven AI capabilities from established vendors',
      content: 'Buying from vendors gives you immediate access to battle-tested capabilities, but you inherit their architectural decisions and limitations. Best suited when AI is an enabler rather than a core differentiator.',
      detailContent: {
        tradeoffs: [
          { gain: 'Deploy working solution in weeks', giveUp: 'Limited customization within vendor constraints' },
          { gain: 'Leverage vendor R&D investment', giveUp: 'Dependency on vendor roadmap and priorities' },
          { gain: 'Predictable costs and SLAs', giveUp: 'Pricing increases and contract negotiations' },
        ],
        questions: [
          'Is the vendor\'s solution close enough to our needs?',
          'What is our acceptable level of vendor dependency?',
          'How do we evaluate vendor long-term viability?',
          'What happens if the vendor pivots away from our use case?',
        ],
        vendorInsights: [
          'Your evaluation found strong utility (USE) scores - the core functionality meets your needs',
          'Exit (EXIT) concerns suggest negotiating data portability terms upfront',
        ],
      },
      children: [
        {
          id: 'buy-speed',
          title: 'Speed to Market',
          type: 'leaf',
          sentiment: 'positive',
          summary: 'Deploy in weeks, not months',
          content: 'Vendor solutions come pre-built, pre-tested, and pre-optimized. What takes 18 months to build can be deployed in 6-8 weeks. In fast-moving markets, this speed advantage can be decisive for capturing market opportunity.',
          detailContent: {
            tradeoffs: [
              { gain: 'Immediate competitive response capability', giveUp: 'Less differentiated solution than competitors using same vendor' },
              { gain: 'Proven integration patterns and documentation', giveUp: 'Vendor\'s architectural decisions become your constraints' },
            ],
            questions: [
              'How time-sensitive is our AI initiative?',
              'What is the cost of delayed market entry?',
              'Can we accept "good enough" while building differentiated capabilities?',
            ],
          },
        },
        {
          id: 'buy-risk',
          title: 'Vendor Risk',
          type: 'leaf',
          sentiment: 'negative',
          summary: 'Lock-in, pricing changes, sunset risk',
          content: 'Vendors have their own business priorities. Pricing can increase at renewal. Features can be deprecated. Companies can be acquired or pivot. Your mission-critical AI capability sits on someone else\'s platform, subject to their decisions.',
          detailContent: {
            tradeoffs: [
              { gain: 'Risk is shared with vendor', giveUp: 'Critical business function depends on external party' },
              { gain: 'Contractual SLAs provide some protection', giveUp: 'Contracts don\'t prevent business model changes' },
            ],
            questions: [
              'What is our exposure if this vendor doubles pricing?',
              'Do we have a 90-day migration plan if needed?',
              'How would vendor acquisition affect our operations?',
            ],
          },
        },
        {
          id: 'buy-proven',
          title: 'Proven Solution',
          type: 'leaf',
          sentiment: 'positive',
          summary: 'Battle-tested at enterprise scale',
          content: 'Leading vendors have processed billions of queries, handled edge cases you haven\'t imagined, and invested hundreds of millions in R&D. You benefit from learnings across their entire customer base without paying for those lessons directly.',
          detailContent: {
            tradeoffs: [
              { gain: 'Benefit from vendor\'s customer-wide learnings', giveUp: 'Your data may train vendor\'s models for competitors' },
              { gain: 'Enterprise security and compliance built-in', giveUp: 'Compliance approach may not match your specific requirements' },
            ],
            questions: [
              'What scale has this vendor demonstrated?',
              'How do they handle our industry\'s specific compliance requirements?',
              'Can we talk to reference customers with similar use cases?',
            ],
          },
        },
        {
          id: 'buy-gap',
          title: 'Capability Gap',
          type: 'leaf',
          sentiment: 'negative',
          summary: 'What you can\'t customize',
          content: 'Every vendor makes opinionated choices about their product. Your evaluation revealed areas where customization is limited. These gaps may be acceptable today but could become strategic constraints as your AI maturity grows.',
          detailContent: {
            tradeoffs: [
              { gain: 'Don\'t need to solve every problem yourself', giveUp: 'Vendor\'s priorities may not align with your roadmap' },
              { gain: 'Can influence vendor through feedback', giveUp: 'No guarantee your requests will be prioritized' },
            ],
            questions: [
              'Which limitations are acceptable vs. deal-breakers?',
              'How might our needs evolve beyond vendor capabilities?',
              'What\'s our plan when we outgrow the vendor?',
            ],
            vendorInsights: [
              'Your CHANGE score indicates limited customization - this may constrain future needs',
            ],
          },
        },
      ],
    },
    {
      id: 'hybrid',
      title: 'HYBRID',
      subtitle: 'Strategic Combination',
      type: 'branch',
      sentiment: 'positive',
      summary: 'Combine vendor speed with in-house differentiation',
      content: 'The hybrid approach leverages vendors for commodity capabilities while building in-house for strategic differentiation. This is often the most sophisticated path, but requires clear thinking about what\'s core vs. context.',
      detailContent: {
        tradeoffs: [
          { gain: 'Optimize both speed and differentiation', giveUp: 'Increased integration complexity' },
          { gain: 'Preserve optionality for future pivots', giveUp: 'Higher ongoing management overhead' },
          { gain: 'Match approach to capability criticality', giveUp: 'Must maintain expertise in multiple systems' },
        ],
        questions: [
          'Which capabilities are truly strategic differentiators?',
          'Do we have the architecture to integrate multiple approaches?',
          'How will we prevent vendor dependency in "commodity" areas from becoming strategic?',
        ],
        vendorInsights: [
          'Consider using evaluated vendors for baseline capabilities while building proprietary components',
          'Your LEARN concerns could be addressed by building high-touch user interactions in-house',
        ],
      },
      children: [
        {
          id: 'hybrid-best',
          title: 'Best of Both',
          type: 'leaf',
          sentiment: 'positive',
          summary: 'Strategic components built in-house',
          content: 'Use vendors for well-understood, commodity capabilities (document processing, basic search). Build in-house for your unique value proposition—the algorithms, interfaces, or workflows that differentiate you from competitors.',
          detailContent: {
            tradeoffs: [
              { gain: 'Focus internal resources on highest-value work', giveUp: 'Must clearly define what\'s "strategic" vs "commodity"' },
              { gain: 'Faster time to market with differentiated offering', giveUp: 'Integration layer becomes critical infrastructure' },
            ],
            questions: [
              'What capabilities would customers pay a premium for?',
              'Where does our unique insight or data create advantage?',
              'Which commodity capabilities have low switching costs?',
            ],
          },
        },
        {
          id: 'hybrid-complexity',
          title: 'Integration Complexity',
          type: 'leaf',
          sentiment: 'negative',
          summary: 'Managing multiple systems',
          content: 'Hybrid approaches mean integrating different systems, managing multiple vendor relationships, and maintaining expertise across diverse technologies. Your platform team becomes critical infrastructure—both for initial integration and ongoing operations.',
          detailContent: {
            tradeoffs: [
              { gain: 'Modular architecture enables future flexibility', giveUp: 'More moving parts to monitor and maintain' },
              { gain: 'Can optimize each component independently', giveUp: 'Cross-system debugging becomes challenging' },
            ],
            questions: [
              'Do we have platform engineering capability to manage integration?',
              'What happens when components from different vendors conflict?',
              'How will we handle version upgrades across systems?',
            ],
          },
        },
        {
          id: 'hybrid-phased',
          title: 'Phased Approach',
          type: 'leaf',
          sentiment: 'positive',
          summary: 'Start with buy, build differentiators over time',
          content: 'Start with vendor solutions to capture immediate value and learn from real usage. As you understand your true requirements, progressively build strategic components in-house. This de-risks the build decision with real market feedback.',
          detailContent: {
            tradeoffs: [
              { gain: 'Learn from production usage before building', giveUp: 'May develop dependencies that are hard to unwind' },
              { gain: 'Generates revenue while building', giveUp: 'Switching costs increase over time' },
            ],
            questions: [
              'What will trigger our decision to start building?',
              'How do we prevent vendor dependency from becoming permanent?',
              'What metrics indicate we\'ve learned enough to build?',
            ],
          },
        },
        {
          id: 'hybrid-exit',
          title: 'Exit Strategy',
          type: 'leaf',
          sentiment: 'neutral',
          summary: 'Keep options open',
          content: 'Design your architecture with portability in mind from day one. Use abstraction layers, standard data formats, and avoid vendor-specific APIs where possible. The goal is to preserve your ability to switch or build without massive migration costs.',
          detailContent: {
            tradeoffs: [
              { gain: 'Future flexibility preserved', giveUp: 'Abstraction layers add development complexity' },
              { gain: 'Negotiating leverage with vendors', giveUp: 'May not leverage vendor-specific optimizations' },
            ],
            questions: [
              'What would a clean migration look like for each vendor?',
              'Are we using standard formats and APIs where available?',
              'How do we test our exit strategy before we need it?',
            ],
            vendorInsights: [
              'Your EXIT score concerns make this especially important',
              'Consider contractual data portability requirements',
            ],
          },
        },
      ],
    },
  ],
};

// Color configurations for different node types
export const NODE_COLORS = {
  root: {
    gradient: 'from-amber-500 to-amber-600',
    solid: 'bg-amber-500',
    border: 'border-amber-500/50',
    glow: 'shadow-amber-500/30',
    bg: 'bg-amber-500/10',
  },
  build: {
    gradient: 'from-violet-500 to-purple-600',
    solid: 'bg-violet-500/60',
    border: 'border-violet-500/50',
    glow: 'shadow-violet-500/30',
    bg: 'bg-violet-500/10',
  },
  buy: {
    gradient: 'from-emerald-500 to-teal-600',
    solid: 'bg-emerald-500/60',
    border: 'border-emerald-500/50',
    glow: 'shadow-emerald-500/30',
    bg: 'bg-emerald-500/10',
  },
  hybrid: {
    gradient: 'from-amber-500 to-orange-600',
    solid: 'bg-amber-500/60',
    border: 'border-amber-500/50',
    glow: 'shadow-amber-500/30',
    bg: 'bg-amber-500/10',
  },
} as const;

// Get color config for a node based on its id
export function getNodeColors(nodeId: string) {
  if (nodeId === 'root') return NODE_COLORS.root;
  if (nodeId.startsWith('build')) return NODE_COLORS.build;
  if (nodeId.startsWith('buy')) return NODE_COLORS.buy;
  if (nodeId.startsWith('hybrid')) return NODE_COLORS.hybrid;
  return NODE_COLORS.root;
}
